import React, { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/services/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  User,
  Mail,
  Phone,
  MapPin,
  Save,
  Lock,
  Eye,
  EyeOff,
  Copy,
  CheckCircle,
  Wallet,
  Building,
  Star,
} from "lucide-react";

export default function EmbajadorPerfil() {
  const { user } = useAuth();
  const [perfil, setPerfil] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);
  
  const [form, setForm] = useState({
    nombre: "",
    telefono: "",
    direccion: "",
    ciudad: "",
  });
  
  // Datos bancarios
  const [datosBancarios, setDatosBancarios] = useState({
    banco: "",
    tipo_cuenta: "",
    numero_cuenta: "",
    titular_cuenta: "",
  });
  const [savingBancarios, setSavingBancarios] = useState(false);
  
  // Password change state
  const [passwordForm, setPasswordForm] = useState({
    current_password: "",
    new_password: "",
    confirm_password: "",
  });
  const [savingPassword, setSavingPassword] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  useEffect(() => {
    fetchPerfil();
  }, []);

  const fetchPerfil = async () => {
    try {
      const res = await api.get("/embajadores/perfil");
      setPerfil(res.data);
      setForm({
        nombre: res.data.nombre || "",
        telefono: res.data.telefono || "",
        direccion: res.data.direccion || "",
        ciudad: res.data.ciudad || "",
      });
      setDatosBancarios({
        banco: res.data.banco || "",
        tipo_cuenta: res.data.tipo_cuenta || "",
        numero_cuenta: res.data.numero_cuenta || "",
        titular_cuenta: res.data.titular_cuenta || "",
      });
    } catch (error) {
      console.error("Error:", error);
      toast.error("Error al cargar el perfil");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/embajadores/perfil", form);
      toast.success("Perfil actualizado");
      fetchPerfil();
    } catch (error) {
      toast.error("Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveBancarios = async (e) => {
    e.preventDefault();
    setSavingBancarios(true);
    try {
      await api.put("/embajadores/perfil", datosBancarios);
      toast.success("Datos bancarios actualizados");
      fetchPerfil();
    } catch (error) {
      toast.error("Error al guardar datos bancarios");
    } finally {
      setSavingBancarios(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (!passwordForm.current_password || !passwordForm.new_password) {
      toast.error("Completa todos los campos");
      return;
    }
    
    if (passwordForm.new_password.length < 6) {
      toast.error("La nueva contraseña debe tener al menos 6 caracteres");
      return;
    }
    
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error("Las contraseñas no coinciden");
      return;
    }
    
    setSavingPassword(true);
    try {
      await api.put("/embajadores/perfil/password", {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password,
      });
      toast.success("Contraseña actualizada correctamente");
      setPasswordForm({ current_password: "", new_password: "", confirm_password: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al cambiar la contraseña");
    } finally {
      setSavingPassword(false);
    }
  };

  const copyAffiliateLink = () => {
    if (perfil?.codigo_afiliado) {
      const link = `${window.location.origin}/catalogo/baruj-principal?ref=${perfil.codigo_afiliado}`;
      navigator.clipboard.writeText(link);
      setCopiedLink(true);
      toast.success("Link copiado al portapapeles");
      setTimeout(() => setCopiedLink(false), 2000);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#C8902A]"></div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-6" data-testid="embajador-perfil">
      <div>
        <h1 className="font-['Playfair_Display'] text-2xl sm:text-3xl text-[#4A2C0A] font-bold">
          Mi Perfil
        </h1>
        <p className="text-[#4A2C0A]/60 font-['Poppins'] text-sm">
          Gestiona tu información personal y de pago
        </p>
      </div>

      {/* Código de Afiliado */}
      <div className="bg-gradient-to-r from-[#C8902A] to-[#A67720] rounded-2xl p-6 text-white">
        <div className="flex items-center gap-3 mb-4">
          <Star className="w-6 h-6" />
          <h2 className="font-['Playfair_Display'] text-xl font-bold">Tu Código de Embajador</h2>
        </div>
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <div className="bg-white/20 backdrop-blur rounded-xl px-6 py-3 font-mono text-xl tracking-wider">
            {perfil?.codigo_afiliado || "---"}
          </div>
          <Button
            onClick={copyAffiliateLink}
            variant="secondary"
            className="bg-white text-[#C8902A] hover:bg-white/90"
          >
            {copiedLink ? (
              <>
                <CheckCircle className="w-4 h-4 mr-2" />
                Copiado
              </>
            ) : (
              <>
                <Copy className="w-4 h-4 mr-2" />
                Copiar Link de Afiliado
              </>
            )}
          </Button>
        </div>
        <p className="text-white/80 text-sm mt-3">
          Nivel: <span className="font-semibold capitalize">{perfil?.nivel || "embajador"}</span>
        </p>
      </div>

      {/* Información Personal */}
      <form onSubmit={handleSave} className="bg-white rounded-2xl p-6 shadow-sm border border-[#E5D4B3] space-y-5">
        <h2 className="font-['Playfair_Display'] text-xl text-[#4A2C0A] font-semibold">
          Información Personal
        </h2>

        <div className="grid md:grid-cols-2 gap-5">
          <div className="space-y-2">
            <Label className="flex items-center gap-2 text-[#4A2C0A]">
              <User className="w-4 h-4 text-[#C8902A]" />
              Nombre completo
            </Label>
            <Input
              value={form.nombre}
              onChange={(e) => setForm({ ...form, nombre: e.target.value })}
              placeholder="Tu nombre"
              className="border-[#E5D4B3]"
            />
          </div>

          <div className="space-y-2">
            <Label className="flex items-center gap-2 text-[#4A2C0A]">
              <Mail className="w-4 h-4 text-[#C8902A]" />
              Correo electrónico
            </Label>
            <Input 
              value={perfil?.email || ""} 
              disabled 
              className="bg-gray-50 border-[#E5D4B3]" 
            />
          </div>

          <div className="space-y-2">
            <Label className="flex items-center gap-2 text-[#4A2C0A]">
              <Phone className="w-4 h-4 text-[#C8902A]" />
              Teléfono / WhatsApp
            </Label>
            <Input
              value={form.telefono}
              onChange={(e) => setForm({ ...form, telefono: e.target.value })}
              placeholder="300 123 4567"
              className="border-[#E5D4B3]"
            />
          </div>

          <div className="space-y-2">
            <Label className="flex items-center gap-2 text-[#4A2C0A]">
              <MapPin className="w-4 h-4 text-[#C8902A]" />
              Ciudad
            </Label>
            <Input
              value={form.ciudad}
              onChange={(e) => setForm({ ...form, ciudad: e.target.value })}
              placeholder="Bogotá, Medellín..."
              className="border-[#E5D4B3]"
            />
          </div>

          <div className="space-y-2 md:col-span-2">
            <Label className="flex items-center gap-2 text-[#4A2C0A]">
              <MapPin className="w-4 h-4 text-[#C8902A]" />
              Dirección
            </Label>
            <Input
              value={form.direccion}
              onChange={(e) => setForm({ ...form, direccion: e.target.value })}
              placeholder="Calle, número, barrio"
              className="border-[#E5D4B3]"
            />
          </div>
        </div>

        <Button
          type="submit"
          disabled={saving}
          className="w-full h-12 bg-[#C8902A] hover:bg-[#A67720]"
        >
          {saving ? (
            <span className="flex items-center gap-2">
              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              Guardando...
            </span>
          ) : (
            <span className="flex items-center gap-2">
              <Save className="w-5 h-5" />
              Guardar Cambios
            </span>
          )}
        </Button>
      </form>

      {/* Datos Bancarios */}
      <form onSubmit={handleSaveBancarios} className="bg-white rounded-2xl p-6 shadow-sm border border-[#E5D4B3] space-y-5">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
            <Wallet className="w-5 h-5 text-green-600" />
          </div>
          <div>
            <h2 className="font-['Playfair_Display'] text-xl text-[#4A2C0A] font-semibold">
              Datos de Pago
            </h2>
            <p className="text-[#4A2C0A]/60 text-sm">Para recibir tus comisiones y retiros</p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-sm text-[#4A2C0A]">Banco</Label>
            <select
              value={datosBancarios.banco}
              onChange={(e) => setDatosBancarios({ ...datosBancarios, banco: e.target.value })}
              className="w-full h-12 px-3 rounded-lg border border-[#E5D4B3] bg-white focus:outline-none focus:ring-2 focus:ring-[#C8902A]/50"
            >
              <option value="">Seleccionar banco</option>
              <option value="bancolombia">Bancolombia</option>
              <option value="davivienda">Davivienda</option>
              <option value="bogota">Banco de Bogotá</option>
              <option value="nequi">Nequi</option>
              <option value="daviplata">Daviplata</option>
            </select>
          </div>
          
          <div className="space-y-2">
            <Label className="text-sm text-[#4A2C0A]">Tipo de Cuenta</Label>
            <select
              value={datosBancarios.tipo_cuenta}
              onChange={(e) => setDatosBancarios({ ...datosBancarios, tipo_cuenta: e.target.value })}
              className="w-full h-12 px-3 rounded-lg border border-[#E5D4B3] bg-white focus:outline-none focus:ring-2 focus:ring-[#C8902A]/50"
            >
              <option value="">Seleccionar tipo</option>
              <option value="ahorros">Cuenta de Ahorros</option>
              <option value="corriente">Cuenta Corriente</option>
            </select>
          </div>
        </div>

        <div className="space-y-2">
          <Label className="text-sm text-[#4A2C0A]">Número de Cuenta</Label>
          <Input
            value={datosBancarios.numero_cuenta}
            onChange={(e) => setDatosBancarios({ ...datosBancarios, numero_cuenta: e.target.value })}
            placeholder="Ej: 123456789012"
            className="h-12 border-[#E5D4B3]"
          />
        </div>

        <div className="space-y-2">
          <Label className="text-sm text-[#4A2C0A]">Titular de la Cuenta</Label>
          <Input
            value={datosBancarios.titular_cuenta}
            onChange={(e) => setDatosBancarios({ ...datosBancarios, titular_cuenta: e.target.value })}
            placeholder="Nombre como aparece en el banco"
            className="h-12 border-[#E5D4B3]"
          />
        </div>

        <Button
          type="submit"
          disabled={savingBancarios}
          className="w-full h-12 bg-green-600 hover:bg-green-700"
        >
          {savingBancarios ? (
            <span className="flex items-center gap-2">
              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              Guardando...
            </span>
          ) : (
            <span className="flex items-center gap-2">
              <Save className="w-5 h-5" />
              Guardar Datos de Pago
            </span>
          )}
        </Button>
      </form>

      {/* Cambio de Contraseña */}
      <form onSubmit={handleChangePassword} className="bg-white rounded-2xl p-6 shadow-sm border border-[#E5D4B3] space-y-5">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
            <Lock className="w-5 h-5 text-red-600" />
          </div>
          <div>
            <h2 className="font-['Playfair_Display'] text-xl text-[#4A2C0A] font-semibold">
              Cambiar Contraseña
            </h2>
            <p className="text-[#4A2C0A]/60 text-sm">Actualiza tu contraseña de acceso</p>
          </div>
        </div>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label className="text-sm text-[#4A2C0A]">Contraseña Actual</Label>
            <div className="relative">
              <Input
                type={showCurrentPassword ? "text" : "password"}
                value={passwordForm.current_password}
                onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })}
                placeholder="Ingresa tu contraseña actual"
                className="h-12 pr-10 border-[#E5D4B3]"
              />
              <button
                type="button"
                onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showCurrentPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-sm text-[#4A2C0A]">Nueva Contraseña</Label>
            <div className="relative">
              <Input
                type={showNewPassword ? "text" : "password"}
                value={passwordForm.new_password}
                onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
                placeholder="Mínimo 6 caracteres"
                className="h-12 pr-10 border-[#E5D4B3]"
              />
              <button
                type="button"
                onClick={() => setShowNewPassword(!showNewPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-sm text-[#4A2C0A]">Confirmar Nueva Contraseña</Label>
            <Input
              type="password"
              value={passwordForm.confirm_password}
              onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })}
              placeholder="Repite la nueva contraseña"
              className="h-12 border-[#E5D4B3]"
            />
          </div>
        </div>

        <Button
          type="submit"
          disabled={savingPassword}
          className="w-full h-12 bg-red-600 hover:bg-red-700"
        >
          {savingPassword ? (
            <span className="flex items-center gap-2">
              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              Actualizando...
            </span>
          ) : (
            <span className="flex items-center gap-2">
              <Lock className="w-5 h-5" />
              Cambiar Contraseña
            </span>
          )}
        </Button>
      </form>
    </div>
  );
}
